"use client"

import { useOurContext } from "@/context/dot-field.context"

import { PageContainerInner } from "@/components/page-container-inner/page-container-inner"
import { PageContainer } from "@/components/page-container/page-container"
import { PageTitle } from "@/components/page-title/page-title"

import { useEffect } from "react"

// Privacy policy page
export default function Privacy() {
  const { setDotFieldIndex } = useOurContext()

  useEffect(() => {
    setDotFieldIndex("random")
  }, [setDotFieldIndex])

  return (
    <PageContainer page="privacy">
      <PageContainerInner>
        <PageTitle title="Privacy Policy" />
        <h4>
          <span>Privacy Position Statement</span>
        </h4>
        <p>
          <span>Last updated: October 15, 2024</span>
        </p>
        <p>
          <span>
            Agency AI, Inc. and its subsidiaries and affiliates (collectively, &ldquo;Agency,&rdquo;
            &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) provides a tool that
            integrates with your email, calendar, and other communication tools to identify tasks
            and to-dos for our users and help them follow up on and complete those.
          </span>
          <span>For more information about how our tool works, see [link to relevant] page.</span>
          <span>We intend for our tool to be used only by business customers.</span>
        </p>
        <p>
          <span>
            This Privacy Position Statement describes our tool as the &ldquo;Services,&rdquo; and
            which may be available through websites, mobile or desktop applications, or software
            plugins to applications provided by other companies.
          </span>
          <span>
            We also have a public-facing website, referred to in this Privacy Position Statement as
            the &ldquo;Website.&rdquo;
          </span>
        </p>
        <p>
          <span>
            This Privacy Position Statement describes how we, the Services, and the Website collect,
            use, disclose, and interact with personal data.
          </span>
          <span>Personal data refers to information identifying a natural person.</span>
          <span>
            This Privacy Position Statement also explains your choices surrounding how we use your
            personal data, which include how you can object to certain uses of the information and
            how you can access and update certain information.
          </span>
        </p>
        <p>
          <span>
            This Privacy Position Statement does not apply to third-party websites, products, or
            services, even if they link to the Website and Services.
          </span>
          <span>
            We recommend you review the privacy practices of those third parties before connecting
            or accessing third party websites and disclosing any personal data. See the section
            titled Links to Other Websites for more information.
          </span>
        </p>
        <p>
          <span>
            Without prejudice to your rights under applicable laws, this Privacy Position Statement
            is not contractual and does not form part of your contract with Agency or its affiliates
            or subsidiaries.
          </span>
        </p>
        <h2>
          <span>Website Visitors</span>
        </h2>
        <p>
          <span>
            If you visit our Website for any purpose, including to use the Services, we may collect,
            use, and disclose personal data as explained in this section of the Privacy Position
            Statement.
          </span>
          <span>We refer to you as a &ldquo;Website Visitor.&rdquo;</span>
        </p>
        <h4>
          <span>Types of Personal Data We May Collect About Website Visitors</span>
        </h4>
        <p>
          <span>
            We may collect the following categories of personal data about website visitors, for the
            purposes stated in the following&nbsp;table.
          </span>
        </p>
        <table>
          <thead>
            <tr>
              <td>
                <h4>
                  <span>Category of Personal Data</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Examples&nbsp;</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Purpose of Collection and Use</span>
                </h4>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    First and last name; contact information; device ID or IP address; and
                    user-generated content (including reviews and feedback)
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Website</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations&nbsp;</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    Browsing and app history; clickstream data; browser and operating system type;
                    navigation paths; date/time stamps; cookie identifiers; clear gifs; Internet
                    service provider (ISP); referring/exit pages; device identifiers; and other
                    information about device characteristics and how you interact with our
                    apps/Sites and Services
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Website</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Geolocation data</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    Device location; geolocation; IP address; cell network data; and/or other
                    similar locational data
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Website&nbsp;</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    Inferences we may infer from other personal data we have collected, which may
                    include preferences and characteristics
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Website&nbsp;</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h4>
          <span>How We May Collect Personal Data About Website Visitors</span>
        </h4>
        <p>
          <span>
            We may collect personal data about website visitors from the sources in the following
            table.
          </span>
        </p>
        <table>
          <thead>
            <tr>
              <td>
                <h4>
                  <span>Sources of Personal Data</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Categories of Personal Data We Collect</span>
                </h4>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Directly from you</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may collect personal data from you when you interact with our Website, submit
                    inquiries or request information from us, or otherwise contact or communicate
                    with us.
                  </span>
                  <span>
                    We may also collect personal data from you when we send you marketing
                    information about our products, services, sales, events, and/or promotions via
                    phone, email, or other means.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information&nbsp;</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Automatically</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may collect personal data from you automatically when you visit the Website
                    or as you interact with us. See the Website Tech section for more information.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Internet or other electronic network activity</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Our service providers and third-party vendors</span>
                  </em>
                </p>
                <p>
                  <span>The Website may use third-party analytics services.</span>
                  <span>
                    These services may record mouse clicks, page views, mouse movements, and
                    scrolling activity and are used by us to analyze trends and administer the
                    Website.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Third parties</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may collect personal data from third-party social media platforms and sites,
                    such as when you engage with our social media pages, online communities, and
                    forums, and when you mention us on your own or other social media pages, online
                    communities, or forums.
                  </span>
                  <span>
                    Please note that online forums may be publicly accessible, and other individuals
                    may view information you post in the forums.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h4>
          <span>Parties To Whom We May Disclose Personal Data About Website Visitors</span>
        </h4>
        <p>
          <span>
            We may disclose personal data about website visitors as shown in the following table.
          </span>
        </p>
        <table>
          <thead>
            <tr>
              <td>
                <h4>
                  <span>Categories of Third-Party Recipients and Purpose for Disclosure</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Categories of Personal Data We May Disclose</span>
                </h4>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Affiliates</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to our business units, subsidiaries, joint
                    ventures, brands, or other companies under our common control (collectively,
                    &ldquo;Affiliates&rdquo;) for the purposes described in this statement.
                  </span>
                  <span>We will require our Affiliates to honor this statement.</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Our service providers and third-party vendors</span>
                  </em>
                </p>
                <p>
                  <span>
                    We use third-party vendors to perform operational functions related to our Sites
                    and Services.
                  </span>
                  <span>
                    Some of these vendors (e.g., marketing automation vendors or hosting providers)
                    may have access to your personal data, but only as necessary to provide services
                    to us.
                  </span>
                  <span>
                    If the third-party vendor is our service provider, we have agreements in place
                    with the vendors requiring your information to be kept confidential and
                    reasonable data security measures to be used to protect it.
                  </span>
                  <span>
                    We also disclose your personal data to vendors (for example, marketing
                    automation vendors or hosting providers) as needed to assist us with business
                    operations, but in such instances, these vendors are contractually required to
                    keep your information confidential.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>
                      Marketing, Advertising, and Analytics Providers for Our Marketing and Brand
                      Purposes
                    </span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to third-party providers for marketing,
                    advertising, and analytics purposes.
                  </span>
                  <span>
                    For additional information regarding our advertising practices, please see
                    the&nbsp;
                  </span>
                  <strong>
                    <span>Advertising</span>
                  </strong>
                  <span>&nbsp;section.</span>
                  <span>
                    We may also disclose personal data to third-party providers for commercial
                    prospecting, product development, and to conduct product and brand perception
                    and awareness surveys.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Government entities&nbsp;</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to government entities and agencies, regulators,
                    law enforcement, and other third parties, including to comply with applicable
                    laws and regulations, to respond to a subpoena or search warrant, pursuant to
                    legal process, to establish or exercise our legal rights, for fraud or
                    crime-prevention purposes, or for the protection of the rights, property, or
                    safety of our company or third parties.
                  </span>
                </p>
                <p>
                  <span>
                    We may also disclose personal data in relation to a known or suspected violation
                    of our terms of use, fraud prevention or other unlawful use, including with
                    entities assisting us with an investigation, or as may be required by applicable
                    law.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Business partners</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may engage third parties to perform certain functions on our behalf.
                  </span>
                  <span>
                    To do so, we may disclose personal data to our third-party business partners and
                    service providers in order to maintain and operate the Website and provide,
                    improve, and personalize the Website, and for other technical and processing
                    functions, such as sending emails on our behalf and technical support.
                  </span>
                  <span>&nbsp;&nbsp;&nbsp;</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Professional Service Firms</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to professional service firms in connection with
                    our legal and regulatory obligations and to establish or exercise our rights and
                    defend against claims, including, for example, auditors, law firms, and
                    consultants.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Successor organizations</span>
                  </em>
                </p>
                <p>
                  <span>
                    Subject to applicable law, we reserve the right to transfer some or all personal
                    data in our possession to a successor organization in the event of a merger,
                    acquisition, bankruptcy, or other sale or transfer of all or a portion of our
                    assets.
                  </span>
                  <span>
                    If any such transaction occurs, the purchaser will be entitled to use and
                    disclose the personal data collected by us in the same manner that we are able
                    to, and the purchaser will assume the rights and obligations regarding personal
                    data as described in this statement.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h4>
          <span>App Users</span>
        </h4>
        <p>
          <span>
            If you use our Services, we may collect, use, and disclose personal data through your
            use of the Services as explained in this section of the Privacy Position Statement.
          </span>
          <span>We refer to you as an &ldquo;App User.&rdquo;</span>
          <span>
            This collection, use, and disclosure of personal data is in addition to the collection,
            use, and disclosure of your information as a Website Visitor.
          </span>
        </p>
        <h4>
          <span>Types of Personal Data We May Collect About App Users</span>
        </h4>
        <p>
          <span>
            We may collect the following categories of personal data, for the purposes stated in the
            following table.
          </span>
        </p>
        <table>
          <thead>
            <tr>
              <td>
                <h4>
                  <span>Category of Personal Data</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Examples&nbsp;</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Purpose of Collection and Use</span>
                </h4>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    First and last name; contact information; device ID or IP address; and
                    user-generated content (including reviews and feedback)
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Services</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations&nbsp;</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    Browsing and app history; clickstream data; browser and operating system type;
                    navigation paths; date/time stamps; cookie identifiers; clear gifs; Internet
                    service provider (ISP); referring/exit pages; device identifiers; and other
                    information about device characteristics and how you interact with the Services
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Services</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Geolocation data</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    Device location; geolocation; IP address; cell network data; and/or other
                    similar locational data
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Services</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Employer; title; professional contact information</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Services</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    Inferences we may infer from other personal data we have collected, which may
                    include preferences and characteristics
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Services</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h4>
          <span>How We May Collect Personal Data About App Users</span>
        </h4>
        <p>
          <span>
            We may collect personal data about App Users from the sources in the following table.
          </span>
        </p>
        <table>
          <thead>
            <tr>
              <td>
                <h4>
                  <span>Sources of Personal Data</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Categories of Personal Data We Collect</span>
                </h4>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Directly from you</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may collect personal data from you when you interact with our Website, submit
                    inquiries or request information from us, or otherwise contact or communicate
                    with us.
                  </span>
                  <span>
                    We may also collect personal data from you when we send you marketing
                    information about our products, services, sales, events, and/or promotions via
                    phone, email, or other means.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information&nbsp;</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Automatically</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may collect personal data from you automatically when you visit the Website
                    or as you interact with us. See the Website Tech section for more information.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Internet or other electronic network activity</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Subscribing organizations</span>
                  </em>
                </p>
                <p>
                  <span>
                    We provide the Services to subscribing organizations, which in turn make the
                    Services available to their employees and members.
                  </span>
                  <span>
                    Subscribing organizations may provide us with personal data about you so that we
                    can provide the Services to you, such as by communicating with you about the
                    Services or validating that you may create an account for the Services.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Our service providers and third-party vendors</span>
                  </em>
                </p>
                <p>
                  <span>The Website may use third-party analytics services.</span>
                  <span>
                    These services may record mouse clicks, page views, mouse movements, and
                    scrolling activity and are used by us to analyze trends and administer the
                    Website.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Third parties</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may collect personal data from third-party social media platforms and sites,
                    such as when you engage with our social media pages, online communities and
                    forums, and when you mention us on your own or other social media pages, online
                    communities, or forums.
                  </span>
                  <span>
                    Please note that online forums may be publicly accessible, and other individuals
                    may view information you post in the forums.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h4>
          <span>Parties To Whom We May Disclose Personal Data About App Users</span>
        </h4>
        <p>
          <span>
            We may disclose personal data about App Users as shown in the following table.
          </span>
        </p>
        <table>
          <thead>
            <tr>
              <td>
                <h4>
                  <span>Categories of Third-Party Recipients and Purpose for Disclosure</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Categories of Personal Data We May Disclose</span>
                </h4>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Affiliates</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to our business units, subsidiaries, joint
                    ventures, brands, or other companies under our common control (collectively,
                    &ldquo;Affiliates&rdquo;) for the purposes described in this statement.
                  </span>
                  <span>We will require our Affiliates to honor this statement.</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Our service providers and third-party vendors</span>
                  </em>
                </p>
                <p>
                  <span>
                    We use third-party vendors to perform operational functions related to our Sites
                    and Services.
                  </span>
                  <span>
                    Some of these vendors (e.g., marketing automation vendors or hosting providers)
                    may have access to your personal data, but only as necessary to provide services
                    to us.
                  </span>
                  <span>
                    If the third-party vendor is our service provider, we have agreements in place
                    with the vendors requiring your information to be kept confidential and
                    reasonable data security measures to be used to protect it.
                  </span>
                  <span>
                    We also disclose your personal data to vendors (for example, marketing
                    automation vendors or hosting providers) as needed to assist us with business
                    operations, but in such instances, these vendors are contractually required to
                    keep your information confidential.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>
                      Marketing, Advertising, and Analytics Providers for Our Marketing and Brand
                      Purposes
                    </span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to third-party providers for marketing,
                    advertising, and analytics purposes.
                  </span>
                  <span>
                    For additional information regarding our advertising practices, please see
                    the&nbsp;
                  </span>
                  <strong>
                    <span>Advertising</span>
                  </strong>
                  <span>&nbsp;section.</span>
                  <span>
                    We may also disclose personal data to third-party providers for commercial
                    prospecting, product development, and to conduct product and branch perception
                    and awareness surveys.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Subscribing organizations</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to subscribing organizations about App Users, so
                    that the subscribing organizations can validate whether an individual may sign
                    up as an App User and to understand their App Users&rsquo; access and use of the
                    Services, or for similar reasons.&nbsp;
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Government entities&nbsp;</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to government entities and agencies, regulators,
                    law enforcement, and other third parties, including to comply with applicable
                    laws and regulations, to respond to a subpoena or search warrant, pursuant to
                    legal process, to establish or exercise our legal rights, for fraud or
                    crime-prevention purposes, or for the protection of the rights, property, or
                    safety of our company or third parties.
                  </span>
                </p>
                <p>
                  <span>
                    We may also disclose personal data in relation to a known or suspected violation
                    of our terms of use, fraud prevention or other unlawful use, including with
                    entities assisting us with an investigation, or as may be required by applicable
                    law.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Business partners</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may engage third parties to perform certain functions on our behalf.
                  </span>
                  <span>
                    To do so, we may disclose personal data to our third-party business partners and
                    service providers in order to maintain and operate the Website and provide,
                    improve, and personalize the Website, and for other technical and processing
                    functions, such as sending emails on our behalf and technical support.
                  </span>
                  <span>&nbsp;&nbsp;&nbsp;</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Professional Service Firms</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to professional service firms in connection with
                    our legal and regulatory obligations and to establish or exercise our rights and
                    defend against claims, including, for example, auditors, law firms, and
                    consultants.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Successor organizations</span>
                  </em>
                </p>
                <p>
                  <span>
                    Subject to applicable law, we reserve the right to transfer some or all personal
                    data in our possession to a successor organization in the event of a merger,
                    acquisition, bankruptcy, or other sale or transfer of all or a portion of our
                    assets.
                  </span>
                  <span>
                    If any such transaction occurs, the purchaser will be entitled to use and
                    disclose the personal data collected by us in the same manner that we are able
                    to, and the purchaser will assume the rights and obligations regarding personal
                    data as described in this statement.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h4>
          <span>Additional Information For App Users</span>
        </h4>
        <p>
          <span>
            If you are an App User, you need to understand that in order to provide the Services to
            you, the Services need to have access to email, calendars, and other communication
            channels.
          </span>
          <span>That access permits the Services to help identify your tasks and to-dos.</span>
          <span>
            This is not the document where we seek your consent or approval to do that; we do that
            in the&nbsp;
          </span>
          <h4>
            <span>EUA</span>
          </h4>
          <span>
            But if you do not consent to the Services&rsquo; access to some or all of your email,
            calendars, and other communication channels, the Services will not work for you.
          </span>
        </p>
        <p>
          <span>
            If you grant the Services access to some or all of your email, calendars, and other
            communications channels, that means that the Services will have access to everything in
            whatever you give the Services access to.
          </span>
          <span>
            For example, if you give the Services access to your email, the Services will have
            access to everything in your email, including messages, message headers, message
            contents, the substance of their emails, and any attachments.
          </span>
        </p>
        <p>
          <span>
            Even though the Services will have access to some or all of your email, calendars, and
            other communication channels, we do not determine how the personal data or substance of
            the information made available to the Services are collected, used, and disclosed.
          </span>
          <span>You and your subscribing organization decides that.</span>
          <span>
            Similarly, we do not intentionally or willfully use the personal data or substance of
            the information made available for our benefit or to train or improve the Service,
            although because the Services use artificial intelligence engines to operate
            independently, we cannot eliminate the possibility that the Services may use information
            run through the Services to inform how the Services respond to information subsequently
            run through it.
          </span>
          <span>
            We may use anonymized usage data about how you interact with the Services or transaction
            data about how the Services connect to your email, calendars, and other communication
            channels, to manage and improve the Services.
          </span>
        </p>
        <p>
          <span>
            We recognize your conversations may contain some of your most sensitive and confidential
            information.
          </span>
          <span>
            That&rsquo;s why we are committed to keeping your information private and secure.
          </span>
          <span>
            Additionally, we believe transparency is important to all meeting participants.
          </span>
          <span>
            As such, you need to comply with local laws and regulations and must always ask for
            consent and indicate when you are recording and transcribing conversations with others.
          </span>
          <span>
            Agency starts recording only when you or a meeting participant presses Record or invites
            Agency to your meeting.
          </span>
          <span>
            When this happens, Agency&rsquo;s AI technology enables it to record and transcribe
            automatically and identify tasks and follow-ups without any human interference.
          </span>
          <span>Your conversations are always private.</span>
          <span>
            When you use our Services to record or transcribe calls, we receive information about
            those calls, such as the date and time of the call and the parties&rsquo; names,
            attendee identifiers, and email addresses.
          </span>
          <span>
            The Services also receive the contents of the call, including the call audio, video, and
            speaker identification.
          </span>
          <span>
            By using our Services to record or transcribe calls, you understand that your call will
            be monitored and recorded.
          </span>
          <span>
            Some states or countries require all parties to consent before monitoring or recording a
            video or audio call.
          </span>
          <span>
            Although we have designed the Services to be as noticeable when connected to a non-email
            or calendar communication channel (such as appearing in the participant list), it is
            your responsibility to obtain whatever permissions are required from other meeting
            participants before you allow the Services to monitor or record a conversation.
          </span>
        </p>
        <p>
          <span>
            Please contact the subscribing organization for more information about how the
            subscribing organization collects, uses, and discloses personal data.
          </span>
        </p>
        <h4>
          <span>Account Administrators</span>
        </h4>
        <p>
          <span>
            Because we offer the Services through subscribing organizations, each subscribing
            organization needs to have one or more individuals who manage the subscribing
            organization&rsquo;s account and user base.
          </span>
          <span>
            If you are managing a subscribing organization&rsquo;s account and user base, we refer
            to you as an &ldquo;Account Administrator,&rdquo; and this section explains how we may
            collect, use, and disclose your personal data.
          </span>
          <span>
            This collection, use, and disclosure of personal data is in addition to the collection,
            use, and disclosure of your information as a Website Visitor.
          </span>
        </p>
        <h4>
          <span>Types of Personal Data We May Collect About Account Administrators</span>
        </h4>
        <p>
          <span>
            We may collect the following categories of personal data about Account Administrators,
            for the purposes stated in the following table.
          </span>
        </p>
        <table>
          <thead>
            <tr>
              <td>
                <h4>
                  <span>Category of Personal Data</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Examples&nbsp;</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Purpose of Collection and Use</span>
                </h4>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    First and last name; contact information; device ID or IP address; and
                    user-generated content (including reviews and feedback)
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Services</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations&nbsp;</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    Browsing and app history; clickstream data; browser and operating system type;
                    navigation paths; date/time stamps; cookie identifiers; clear gifs; Internet
                    service provider (ISP); referring/exit pages; device identifiers; and other
                    information about device characteristics and how you interact with the Services
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Services</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Geolocation data</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    Device location; geolocation; IP address; cell network data; and/or other
                    similar locational data
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Services</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Employer; title; professional contact information</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Services</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
              <td>
                <p>
                  <span>
                    Inferences we may infer from other personal data we have collected, which may
                    include preferences and characteristics
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Provide and enhance the Services</span>
                </p>
                <p>
                  <span>Detect and prevent fraud</span>
                </p>
                <p>
                  <span>Market or advertise to you</span>
                </p>
                <p>
                  <span>Communicate with you</span>
                </p>
                <p>
                  <span>Comply with legal or regulatory obligations</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h4>
          <span>How We May Collect Personal Data About Account Administrators</span>
        </h4>
        <p>
          <span>
            We may collect personal data about Account Administrators from the sources in the
            following table.
          </span>
        </p>
        <table>
          <thead>
            <tr>
              <td>
                <h4>
                  <span>Sources of Personal Data</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Categories of Personal Data We Collect</span>
                </h4>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Directly from you</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may collect personal data from you when you interact with our Website, submit
                    inquiries or request information from us, or otherwise contact or communicate
                    with us.
                  </span>
                  <span>
                    We may also collect personal data from you when we send you marketing
                    information about our products, services, sales, events, and/or promotions via
                    phone, email, or other means.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information&nbsp;</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Automatically</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may collect personal data from you automatically when you visit the Website
                    or as you interact with us.
                  </span>
                  <span>See the&nbsp;</span>
                  <h4>
                    <span>Website Tech </span>
                  </h4>
                  <span>section for more information.</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Internet or other electronic network activity</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Our service providers and third-party vendors</span>
                  </em>
                </p>
                <p>
                  <span>The Website may use third-party analytics services.</span>
                  <span>
                    These services may record mouse clicks, page views, mouse movements, and
                    scrolling activity and are used by us to analyze trends and administer the
                    Website.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h4>
          <span>Parties To Whom We May Disclose Personal Data About Account Administrators</span>
        </h4>
        <p>
          <span>
            We disclose personal data about Account Administrators as shown in the following table.
          </span>
        </p>
        <table>
          <thead>
            <tr>
              <td>
                <h4>
                  <span>Categories of Third-Party Recipients and Purpose for Disclosure</span>
                </h4>
              </td>
              <td>
                <h4>
                  <span>Categories of Personal Data We May Disclose</span>
                </h4>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Affiliates</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to our business units, subsidiaries, joint
                    ventures, brands, or other companies under our common control (collectively,
                    &ldquo;Affiliates&rdquo;) for the purposes described in this statement.
                  </span>
                  <span>We will require our Affiliates to honor this statement.</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Our service providers and third-party vendors</span>
                  </em>
                </p>
                <p>
                  <span>
                    We use third-party vendors to perform operational functions related to our Sites
                    and Services.
                  </span>
                  <span>
                    Some of these vendors (e.g., marketing automation vendors or hosting providers)
                    may have access to your personal data, but only as necessary to provide services
                    to us.
                  </span>
                  <span>
                    If the third-party vendor is our service provider, we have agreements in place
                    with the vendors requiring your information to be kept confidential and
                    reasonable data security measures to be used to protect it.
                  </span>
                  <span>
                    We also disclose your personal data to vendors (for example, marketing
                    automation vendors or hosting providers) as needed to assist us with business
                    operations, but in such instances, these vendors are contractually required to
                    keep your information confidential.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>
                      Marketing, Advertising, and Analytics Providers for Our Marketing and Brand
                      Purposes
                    </span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to third-party providers for marketing,
                    advertising, and analytics purposes.
                  </span>
                  <span>
                    For additional information regarding our advertising practices, please see
                    the&nbsp;
                  </span>
                  <h4>
                    <span>Advertising</span>
                  </h4>
                  <span>&nbsp;section.</span>
                  <span>
                    We may also disclose personal data to third-party providers for commercial
                    prospecting, product development, and to conduct product and branch perception
                    and awareness surveys.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Government entities&nbsp;</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to government entities and agencies, regulators,
                    law enforcement, and other third parties, including to comply with applicable
                    laws and regulations, to respond to a subpoena or search warrant, pursuant to
                    legal process, to establish or exercise our legal rights, for fraud or
                    crime-prevention purposes, or for the protection of the rights, property, or
                    safety of our company or third parties.
                  </span>
                </p>
                <p>
                  <span>
                    We may also disclose personal data in relation to a known or suspected violation
                    of our terms of use, fraud prevention or other unlawful use, including with
                    entities assisting us with an investigation, or as may be required by applicable
                    law.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Business partners</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may engage third parties to perform certain functions on our behalf.
                  </span>
                  <span>
                    To do so, we may disclose personal data to our third-party business partners and
                    service providers in order to maintain and operate the Website and provide,
                    improve, and personalize the Website, and for other technical and processing
                    functions, such as sending emails on our behalf and technical support.
                  </span>
                  <span>&nbsp;&nbsp;&nbsp;</span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Professional Service Firms</span>
                  </em>
                </p>
                <p>
                  <span>
                    We may disclose personal data to professional service firms in connection with
                    our legal and regulatory obligations and to establish or exercise our rights and
                    defend against claims, including, for example, auditors, law firms, and
                    consultants.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Geolocation data</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
                <p>
                  <span>Inference information</span>
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <em>
                    <span>Successor organizations</span>
                  </em>
                </p>
                <p>
                  <span>
                    Subject to applicable law, we reserve the right to transfer some or all personal
                    data in our possession to a successor organization in the event of a merger,
                    acquisition, bankruptcy, or other sale or transfer of all or a portion of our
                    assets.
                  </span>
                  <span>
                    If any such transaction occurs, the purchaser will be entitled to use and
                    disclose the personal data collected by us in the same manner that we are able
                    to, and the purchaser will assume the rights and obligations regarding personal
                    data as described in this statement.
                  </span>
                </p>
              </td>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
                <p>
                  <span>Internet or other electronic activity information</span>
                </p>
                <p>
                  <span>Electronic information</span>
                </p>
                <p>
                  <span>Professional or employment-related information</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h4>Everyone Else</h4>
        <p>
          <span>
            The Services rely upon access to an App User&rsquo;s email, calendar, or other
            communication channels, including the content of those communications, in order to
            identify tasks and to-dos for the App User.
          </span>
          <span>
            This means, if you communicate with an App User through a channel connected to the
            Services, the substance and content of your communications with the App User are
            accessible to the Services.
          </span>
          <span>
            For example, an App User may elect to record an online meeting you are attending; if so,
            Agency&rsquo;s AI technology enables it to record and transcribe automatically, and
            identify tasks and follow-ups, without any human interference, and if you attend a
            meeting or join another communication channel with Agency present, you understand and
            consent to the Service&rsquo;s monitoring and recording of your communications for
            purposes of providing the Services.
          </span>
        </p>
        <p>
          <span>
            Even though the Services have access to the content of communications exchanged with an
            App User, those contents are used only to provide the Services to the App User and are
            not used to improve the Services or any of its components or for our benefit.
          </span>
          <span>
            We do not determine how and why information is collected, used, or disclosed, or the
            purposes and means of processing.
          </span>
          <span>
            For more information about how your information and communications may be collected,
            used, and disclosed, please contact the relevant App User or their subscribing
            organization.
          </span>
        </p>
        <h4>
          <span>Google User Data</span>
        </h4>
        <p>
          <span>
            The use and transfer to any other app of information received from Google APIs will
            adhere to the Google API Services User Data Policy (
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              rel="noopener noreferrer"
              target="_blank"
            >
              see here
            </a>
            ), including the Limited User Requirements.
          </span>
        </p>
        <p>
          <span>
            We affirm that any data accessed through Google Workspace APIs will not be used to train
            or improve any generalized AI or machine learning models.&quot;
          </span>
        </p>
        <h4>
          <span>Aggregated, De-Identified, or Anonymized Data</span>
        </h4>
        <p>
          <span>
            We may create aggregated, de-identified, or anonymized information from personal data by
            removing certain data components (such as your name, email address, or linkable tracking
            ID) that makes the data identifiable, or through aggregation, obfuscation, or other
            means.
          </span>
          <span>
            For example, we may use aggregated, de-identified, or anonymized information to
            understand how to improve or enhance the Website or Services.
          </span>
          <span>
            Subject to applicable law, our use of such aggregated, de-identified, or anonymized
            information is not personal data or subject to this Privacy Position Statement.
          </span>
        </p>
        <h4>
          <span>Your Choices</span>
        </h4>
        <p>
          <span>You have certain choices with respect to your personal data.</span>
        </p>
        <p>
          <span>
            We may use your contact information to send you email messages regarding updates to the
            Website or Services, such as promotional offers or to communicate with you about us.
          </span>
          <span>
            You may opt-out of receiving these emails at any time via the unsubscribe link contained
            in the email message.
          </span>
        </p>
        <p>
          <span>
            We will communicate with you only according to your preferences provided to us.
          </span>
          <span>
            We may use your contact information to send you messages regarding updates to the
            Website and Services.
          </span>
          <span>
            You may opt-out of receiving these messages by following the unsubscribe link contained
            in the message, or by contacting us through the information contained in
            the&nbsp;Contact Us section.
          </span>
          <span>
            You may not be able to opt-out of receiving certain messages from us, such as
            transactional communications or legal notices.
          </span>
        </p>
        <h4>
          <span>Non-US Visitors and Users</span>
        </h4>
        <p>
          <span>
            We are based in the United States and provide the Services from the United States, which
            may have data protection laws that are different from the laws where you live.
          </span>
          <span>
            If you are located outside of the United States, any information you provide to us will
            be transferred to, processed, and/or maintained in the United States.
          </span>
          <span>
            By accessing our Website, or using our Services, you consent to the transfer of your
            personal data to the United States.
          </span>
        </p>
        <h4>
          <span>Data Retention</span>
        </h4>
        <p>
          <span>
            We store the information we receive as described in this Privacy Position Statement for
            as long as you use our Services or as necessary to fulfill the purpose(s) for which it
            was collected, provide our Services, resolve disputes, establish legal defenses, conduct
            audits, pursue legitimate business purposes, enforce our agreements, and comply with
            applicable laws.
          </span>
        </p>
        <h4>
          <span>Links to Other Websites</span>
        </h4>
        <p>
          <span>
            Our Website may include social network sharing widgets that may provide information to
            their associated social networks or third parties about your interactions with our web
            pages that you visit, even if you do not click on or otherwise interact with the plug-in
            or widget.
          </span>
          <span>
            Information is transmitted from your browser and may include an identifier assigned by
            the social network or third party, information about your browser type, operating
            system, device type, IP address, and the URL of the web page where the widget appears.
          </span>
          <span>
            If you use social networking tools or visit social networking sites, we encourage you to
            read their privacy disclosures to learn what information they collect, use, and
            disclose.
          </span>
        </p>
        <h4>
          <span>Children under Age 13</span>
        </h4>
        <p>
          <span>
            The Website and the Services offered are not directed to or intended for use by children
            under 13 years of age.
          </span>
          <span>
            We do not knowingly collect or solicit personal data from anyone under the age of 13, or
            knowingly allow such persons to register for our services, without parental consent.
          </span>
          <span>No one under age 13 may provide any personal data to us or on the Websites.</span>
          <span>
            If you are under 13, please do not attempt to register for the Websites or send any
            information about yourself to us, including your name, address, telephone number, or
            email address.
          </span>
          <span>
            If we learn that we have inadvertently collected personal data from an individual under
            age 13 without parental consent, we will take steps to delete the data as permitted by
            law.
          </span>
          <span>
            If you believe that we might have any information from or about an individual under age
            13, please contact us using the information in the&nbsp;
          </span>
        </p>
        <h4>
          <span>Changes to Our Privacy Position Statement</span>
        </h4>
        <p>
          <span>We may update our Privacy Position Statement from time to time.</span>
          <span>
            The &ldquo;effective date&rdquo; at the top of this page indicates when our Privacy
            Position Statement was last revised.
          </span>
          <span>Any changes are effective when we post the revised statement on our Website.</span>
          <span>
            We encourage you to routinely review this page to learn about updates to our privacy
            practices.
          </span>
        </p>
        <h4>
          <span>Contact Us</span>
        </h4>
        <p>
          <span>
            If you have any questions, comments, complaints, or concerns about our Privacy Position
            Statement, or if you would like to exercise your rights with respect to your personal
            information, please email us at&nbsp;
          </span>
          <a href="mailto:privacy@agency.inc">
            <u>
              <span>privacy@agency.inc</span>
            </u>
          </a>
          <span>
            &nbsp;or write to us at Agency AI, Inc., 68 Harrison Ave Ste 605, PMB 646292, Boston,
            Massachusetts 02111-1929.
          </span>
          <span>We will endeavor to respond to your inquiry as soon as possible.</span>
        </p>
      </PageContainerInner>
    </PageContainer>
  )
}
